import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import { Link2, Shield, Check, Lock, RotateCcw } from 'lucide-react'
import to from 'await-to-js'

import FbButton from 'components/FbUI/FbButton'
import {
  useGetGlobalCampaignSettings,
  useUpdateGlobalCampaignSettings,
} from 'features/campaigns/global_settings_api'

const OPT_OUT_SAFETY_ITEMS = [
  {
    title: 'Enable opt-out handling',
    description: 'Process unsubscribe requests automatically',
  },
  {
    title: 'Auto-remove unsubscribes',
    description: 'Immediately stop sending to contacts who unsubscribe',
  },
  {
    title: 'Stop sequence on reply',
    description: 'Stop sending follow-ups when a contact replies',
  },
  {
    title: 'Bounce detection',
    description:
      'Hard bounces stop the sequence immediately. High bounce rates temporarily pause sending to protect your domain.',
  },
] as const

export function SequencingSettings() {
  const { data: settings, isLoading } = useGetGlobalCampaignSettings()
  const updateMutation = useUpdateGlobalCampaignSettings()

  const [unsubscribeText, setUnsubscribeText] = useState<string | null>(null)
  const [unsubscribeError, setUnsubscribeError] = useState('')

  // Derive displayed values from API data + local overrides
  const displayedUnsubscribeText =
    unsubscribeText ?? settings?.unsubscribe_text ?? ''

  const handleRestoreDefault = useCallback(() => {
    if (settings?.default_unsubscribe_text) {
      setUnsubscribeText(settings.default_unsubscribe_text)
      setUnsubscribeError('')
    }
  }, [settings?.default_unsubscribe_text])

  const handleSaveUnsubscribe = async () => {
    const text = displayedUnsubscribeText.trim()
    if (!text.toLowerCase().includes('unsubscribe')) {
      setUnsubscribeError("Opt-out text must include the word 'unsubscribe'.")
      return
    }
    setUnsubscribeError('')

    const [err] = await to(
      updateMutation.mutateAsync({ unsubscribe_text: text })
    )
    if (err) {
      const errorMessage =
        (err as any)?.response?.data?.detail ||
        'Failed to save unsubscribe text'
      toast.error(errorMessage)
      return
    }

    setUnsubscribeText(null)
    toast.success('Unsubscribe text saved')
  }

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-8 py-8 animate-pulse">
          <div className="mb-8">
            <div className="h-6 w-36 bg-surface-disabled rounded" />
            <div className="h-4 w-80 bg-surface-disabled rounded mt-2" />
          </div>
          <div className="space-y-6">
            <div className="bg-surface rounded-xl border border-line p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-9 rounded-lg bg-surface-disabled" />
                <div className="space-y-1.5">
                  <div className="h-4 w-40 bg-surface-disabled rounded" />
                  <div className="h-3 w-56 bg-surface-disabled rounded" />
                </div>
              </div>
              <div className="h-3 w-24 bg-surface-disabled rounded mb-2" />
              <div className="h-20 w-full bg-surface-disabled rounded" />
            </div>
            <div className="bg-surface rounded-xl border border-line p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="size-9 rounded-lg bg-surface-disabled" />
                <div className="space-y-1.5">
                  <div className="h-4 w-32 bg-surface-disabled rounded" />
                  <div className="h-3 w-64 bg-surface-disabled rounded" />
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-12 w-full bg-surface-disabled rounded" />
                <div className="h-12 w-full bg-surface-disabled rounded" />
                <div className="h-12 w-full bg-surface-disabled rounded" />
                <div className="h-12 w-full bg-surface-disabled rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-2xl mx-auto px-8 py-8">
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-fg">Sequencing</h2>
          <p className="text-fg-secondary text-sm mt-1">
            These settings apply to all campaigns. Changes here affect every
            active and future campaign.
          </p>
        </div>

        <div className="space-y-6">
          {/* Unsubscribe Method */}
          <div className="bg-surface rounded-xl border border-line p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-9 rounded-lg bg-brand/10 flex items-center justify-center">
                <Link2 className="size-4 text-brand" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-fg">
                  Unsubscribe Method
                </h3>
                <p className="text-xs text-fg-secondary">
                  How recipients can opt out of future emails
                </p>
              </div>
            </div>

            <div>
              <label className="text-xxs text-fg-secondary uppercase tracking-wider block mb-2">
                Unsubscribe text
              </label>
              <p className="text-xxs text-fg-secondary mb-2">
                Your opt-out text must include the word &apos;unsubscribe&apos;
                and a clear action (e.g. &apos;reply&apos;).
              </p>
              <textarea
                value={displayedUnsubscribeText}
                maxLength={1000}
                onChange={(e) => {
                  setUnsubscribeText(e.target.value)
                  setUnsubscribeError('')
                }}
                className="w-full min-h-[80px] px-3 py-2 text-sm border border-line rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
              />
              {unsubscribeError && (
                <p className="text-xxs text-error mt-1">{unsubscribeError}</p>
              )}
              <div className="flex items-center justify-between mt-2">
                <button
                  type="button"
                  onClick={handleRestoreDefault}
                  className="flex items-center gap-1.5 text-xs text-brand hover:underline"
                >
                  <RotateCcw className="size-3" />
                  Restore default
                </button>
                <FbButton
                  size="sm"
                  onClick={handleSaveUnsubscribe}
                  disabled={
                    unsubscribeText === null || updateMutation.isPending
                  }
                  loading={updateMutation.isPending}
                >
                  Save
                </FbButton>
              </div>
            </div>
          </div>

          {/* Opt-Out & Safety */}
          <div className="bg-surface rounded-xl border border-line p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-9 rounded-lg bg-brand/10 flex items-center justify-center">
                <Shield className="size-4 text-brand" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-fg">
                  Opt-Out & Safety
                </h3>
                <p className="text-xs text-fg-secondary">
                  Global protection settings for all campaigns
                </p>
              </div>
            </div>
            <div className="space-y-1">
              {OPT_OUT_SAFETY_ITEMS.map((item, index) => (
                <div
                  key={item.title}
                  className={`flex items-start justify-between py-3 ${
                    index < OPT_OUT_SAFETY_ITEMS.length - 1
                      ? 'border-b border-line'
                      : ''
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="size-5 rounded-full bg-brand flex items-center justify-center shrink-0 mt-0.5">
                        <Check
                          className="size-3 text-fg-inverse"
                          strokeWidth={3}
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-fg">{item.title}</p>
                        <p className="text-xs text-fg-secondary">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Lock className="size-4 text-fg-secondary ml-3 mt-0.5" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
